import type { RoiId } from '~/domain/changes/ids';
import {
    Box,
    Divider,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import RoiColor from '~/components/Structures/RoiColor';
import { useContouringToolsParked } from '~/components/toolbar/shared/useParkedContouringTool';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';
import { editBlockedReasonId } from '~/components/Structures/editBlockedMessage';
import StructureColorMenuItem from '~/components/Structures/StructureColorMenuItem';
import { countClearedSlicesWithContent } from '~/engine/data/structure/deleteScopePlan';
import { isImageSlicePlane } from '~/engine/data/structure/planeSliceResolution';
import { sliceRefusalMessageId } from '~/hooks/structure-set/sliceRefusal';
import { colorEditWithheldReasonId } from '~/hooks/structure-set/useStructureColorEdit';
import {
    ContourDeletionMenuState,
    structureWriteInFlightState,
} from '~/state/atoms/contourDeletionAtoms';

// ── The deletions offered on the structure itself ────────────────────
//
// Every item names the structure it would act on, and every item that is
// withheld says why beside itself. A right-click that produces nothing —
// or a greyed row with no reason — is indistinguishable from a broken
// build, and SR-WOR-H-24-1 requires the reason to be surfaced.
//
// The menu ALWAYS acts on the selected structure. `findRoiWithinMm` picks the
// nearest visible contour edge, which where two structures abut can be a
// neighbour's; acting on that would bypass every gate in
// useStructureToolGating, all of which are evaluated for the selection.
// When the pick found a different structure the menu offers to select it
// instead, so the change of target is an act the user takes and sees.

/** An item, and the reason it is withheld if it is. */
interface MenuAction {
    labelId: string;
    labelValues?: Record<string, string | number>;
    onClick: () => void;
    /** Present ⇒ the item is disabled and this is shown under it. */
    withheldReason?: { id: string; values?: Record<string, string | number> };
    testId: string;
}

export interface ContourDeletionMenuProps {
    menu: ContourDeletionMenuState;
    /**
     * Whether the view the menu was asked from drew the edited image, alone
     * or blended, when it opened.
     */
    pressedViewShowsImage: boolean;
    onClose: () => void;
    onSelectPicked: (roiId: RoiId) => void;
    onDeleteCurve: () => void;
    onClearSlice: () => void;
    onClearStructure: () => void;
    onChangeColor: () => void;
}

export default function ContourDeletionMenu({
    menu,
    pressedViewShowsImage,
    onClose,
    onSelectPicked,
    onDeleteCurve,
    onClearSlice,
    onClearStructure,
    onChangeColor,
}: ContourDeletionMenuProps) {
    const intl = useIntl();
    const { environment } = useActiveEditTarget();
    const isWriting = useRecoilValue(structureWriteInFlightState);
    const isParked = useContouringToolsParked();
    const {
        selectedRoiId,
        selectedRoi,
        isPointRoi,
        isTilted,
        selectionEditBlocker,
        isBulkSelectionActive,
        hasSelectedRoi,
        isPrimaryImageIncomplete,
        imageIncompleteReason,
    } = useStructureToolGating();

    const roi =
        selectedRoiId === null
            ? undefined
            : environment?.structureSet?.getRoi(selectedRoiId);
    const name = roi?.name ?? selectedRoi?.name ?? '';

    /**
     * A reason the WHOLE menu is unavailable, in the same precedence
     * ContouringTools applies (ContouringTools.tsx). Copied rather than
     * re-ordered so one surface cannot report a different first cause
     * than another for the same state.
     */
    const menuReason = (():
        | { id: string; values?: Record<string, string> }
        | undefined => {
        if (isBulkSelectionActive) {
            return { id: 'tool.disabled.bulkSelection' };
        }
        if (selectionEditBlocker) {
            return { id: editBlockedReasonId(selectionEditBlocker) };
        }
        if (isPointRoi) {
            return { id: 'tool.disabled.pointRoi' };
        }
        if (!hasSelectedRoi) {
            return { id: 'tool.disabled.noRoi' };
        }
        if (isParked) {
            // SR-WOR-H-24-1 already suspends the tools that write to a
            // structure while a positioned region is on screen; this is
            // one of them.
            return { id: 'tool.disabled.regionBox' };
        }
        if (isWriting) {
            return { id: 'contourDeletion.disabled.busy' };
        }
        if (selectedRoi && !selectedRoi.visible) {
            // Nothing of the structure is drawn, so there is no result
            // for the user to check against afterwards.
            return { id: 'contourDeletion.disabled.hidden', values: { name } };
        }
        return undefined;
    })();

    // The incomplete-image reason arrives already formatted (it carries
    // slice counts), and it comes first because it is the one condition
    // the user cannot act on at all: no selection change fixes an image
    // that is still arriving.
    const blockedSentence = isPrimaryImageIncomplete
        ? imageIncompleteReason
        : menuReason
          ? intl.formatMessage({ id: menuReason.id }, menuReason.values)
          : undefined;

    const sdf = roi?.texture?.signedDistanceField;
    const slicesHoldingContour = sdf
        ? countClearedSlicesWithContent(sdf, { kind: 'whole-texture' })
        : 0;

    // Refused on the same predicate the write refuses on: one that covers
    // a tilted series, a sagittal or coronal main view and a rotated
    // frame in a single question.
    const sliceResolves =
        menu.planeAtOpen !== null &&
        !!environment?.image &&
        isImageSlicePlane(environment.image, menu.planeAtOpen);
    // A view that draws the fused secondary alone shows no slice of the
    // edited image, although its plane is one.
    const sliceWithheldReason = !sliceResolves
        ? sliceRefusalMessageId({ tilted: isTilted, addressedBy: 'view' })
        : !pressedViewShowsImage
          ? 'postProcessing.delete.tileShowsOtherImage'
          : undefined;

    const pickedOther =
        menu.pickedRoiId !== undefined && menu.pickedRoiId !== selectedRoiId;

    // Recolouring changes no contour, so none of the reasons that
    // withhold the deletions withhold it.
    const colorWithheldId =
        selectedRoi &&
        colorEditWithheldReasonId(selectedRoi, isBulkSelectionActive);

    const actions: MenuAction[] = [];

    if (pickedOther) {
        const pickedName =
            environment?.structureSet?.getRoi(menu.pickedRoiId!)?.name ?? '';
        actions.push({
            labelId: 'contourDeletion.selectStructure',
            labelValues: { name: pickedName },
            onClick: () => onSelectPicked(menu.pickedRoiId!),
            testId: 'contour-deletion-select',
        });
    }

    actions.push({
        labelId: 'contourDeletion.deleteCurve',
        onClick: onDeleteCurve,
        withheldReason: blockedSentence
            ? undefined
            : pickedOther
              ? {
                    id: 'contourDeletion.disabled.notSelected',
                    values: { name },
                }
              : menu.curveWindow === undefined
                ? { id: 'contourDeletion.disabled.noCurve', values: { name } }
                : undefined,
        testId: 'contour-deletion-curve',
    });

    actions.push({
        labelId: 'contourDeletion.clearSlice',
        onClick: onClearSlice,
        withheldReason:
            blockedSentence || !sliceWithheldReason
                ? undefined
                : { id: sliceWithheldReason },
        testId: 'contour-deletion-slice',
    });

    actions.push({
        labelId: 'contourDeletion.clearStructure',
        labelValues: { slices: slicesHoldingContour },
        onClick: onClearStructure,
        withheldReason: blockedSentence
            ? undefined
            : slicesHoldingContour === 0
              ? { id: 'contourDeletion.disabled.empty', values: { name } }
              : undefined,
        testId: 'contour-deletion-structure',
    });

    return (
        <Menu
            open
            onClose={onClose}
            anchorReference="anchorPosition"
            anchorPosition={menu.anchorPosition}
            data-testid="contour-deletion-menu"
            MenuListProps={{ dense: true }}
        >
            {/* The structure every item below acts on, stated once at the
                top in its display colour: the pointer may be nearer a
                neighbour's contour, and which structure is about to lose
                contour is the one thing the user must not have to infer. */}
            <Box
                sx={{
                    px: 2,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}
                data-testid="contour-deletion-header"
            >
                {selectedRoi && (
                    <RoiColor
                        color={selectedRoi.color}
                        size={10}
                        isMarker={selectedRoi.isPointRoi}
                    />
                )}
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                >
                    {name}
                </Typography>
            </Box>
            {selectedRoi && (
                <StructureColorMenuItem
                    roi={selectedRoi}
                    onClick={onChangeColor}
                    disabled={colorWithheldId !== undefined}
                    withheldReasonId={colorWithheldId}
                />
            )}
            {selectedRoi && <Divider />}
            {blockedSentence && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ px: 2, pb: 0.5, display: 'block', maxWidth: 320 }}
                    data-testid="contour-deletion-blocked"
                >
                    {blockedSentence}
                </Typography>
            )}
            <Divider />
            {actions.map((action) => {
                const disabled = !!blockedSentence || !!action.withheldReason;
                return (
                    <MenuItem
                        key={action.testId}
                        disabled={disabled}
                        onClick={action.onClick}
                        data-testid={action.testId}
                    >
                        <ListItemText
                            primary={
                                <FormattedMessage
                                    id={action.labelId}
                                    values={action.labelValues}
                                />
                            }
                            secondary={
                                action.withheldReason && !blockedSentence ? (
                                    <FormattedMessage
                                        id={action.withheldReason.id}
                                        values={action.withheldReason.values}
                                    />
                                ) : undefined
                            }
                            secondaryTypographyProps={{
                                sx: { maxWidth: 300, whiteSpace: 'normal' },
                            }}
                        />
                    </MenuItem>
                );
            })}
        </Menu>
    );
}
